import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useAppTheme } from './hooks/useAppTheme';
import { useAuth } from './hooks/auth/useAuth';
import { ROUTES } from './config/constants/routes';

// Components
import LoginForm from './components/auth/LoginForm';
import ForgetPassword from './components/auth/ForgetPasswordForm';
import AccountSetupForm from './components/auth/AccountSetupForm';
import NewClientLogin from './components/auth/NewClientLogin';
import DashboardLayout from './components/layout/DashboardLayout';
import AuthCallback from './components/auth/AuthCallback';
import LogoutCallback from './components/auth/LogoutCallback';

// Views
import SuperAdminView from './views/SuperAdmin/SuperAdminView';
import DealersView from './views/SuperAdmin/DealersView';
import { DealerAdminView } from './views/DealerAdmin';
import DashboardView from './views/Dashboard/DashboardView';
import BookingsView from './views/Bookings/BookingsView';
import BookingDetailView from './views/Bookings/BookingDetailView';
import ResourcesView from './views/Resources/ResourcesView';
import CustomersView from './views/Customers/CustomersView';
import ServicesView from './views/Services/ServicesView';
import KnowledgeBaseView from './views/KnowledgeBase/KnowledgeBaseView';
import SettingsView from './views/Settings/SettingsView';
import { AUTH_CONSTANTS } from './config/constants';
import CustomerView from './views/CustomerView';
import ChatLogsView from './views/ChatLogs/ChatLogsView';
import { FormsView, FormCreateView, FormEditView, FormDetailView } from './views/Forms';
import PhoneView from './views/Phone/PhoneView';
import RoutingView from './views/Phone/RoutingView';
import SignupForm from './components/public/SignupForm';
import ActivateView from './components/public/ActivateView';
import PendingRequestsView from './views/SuperAdmin/components/PendingRequestsView';
import UserActivateView from './components/auth/UserActivateView';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import BillingView from './views/Settings/Billings/BillingView';
import PersonalizationSettings from './views/Settings/PersonalizationSettings';
import PaymentSettingsView from './views/Settings/Payment/PaymentSettingsView';

// Protected Route Component
const ProtectedRoute = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Outlet /> : <Navigate to={ROUTES.ROOT} />;
};
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
    (() => {
      throw new Error('VITE_STRIPE_PUBLISHABLE_KEY is not defined');
    })()
);

export default function App() {
  const { isAuthenticated, isAdmin, isDealerAdmin, role, clientId } = useAuth();
  const theme = useAppTheme();

  const AdminRoute = () => {
    return isAuthenticated && isAdmin ? <Outlet /> : <Navigate to={ROUTES.ROOT} />;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Elements stripe={stripePromise}>
        <Routes>
          <Route
            path={ROUTES.ROOT}
            element={
              isAuthenticated ? (
                isAdmin ? (
                  <SuperAdminView />
                ) : isDealerAdmin ? (
                  <DealerAdminView />
                ) : (
                  <CustomerView clientId={clientId} />
                )
              ) : (
                <LoginForm />
              )
            }
          />
          <Route path="new-client-login" element={<NewClientLogin />} />
          <Route path="signup" element={<SignupForm />} />
          <Route path="activate/:id" element={<ActivateView />} />
          {/* OAuth callback routes */}
          <Route path="auth/callback" element={<AuthCallback />} />
          <Route path="logout" element={<LogoutCallback />} />
          {/* /useractivate/${clientId}/${userInfo.uuid} */}
          <Route path="useractivate/:id/:uuid" element={<UserActivateView />} />
          <Route path="forget-password" element={<ForgetPassword />} />
          <Route path="account-setup" element={<AccountSetupForm />} />
          <Route element={<ProtectedRoute />}>
            <Route path="subscriptions" element={<BillingView />} />
          </Route>

          <Route element={<AdminRoute />}>
            <Route path="pending-requests" element={<PendingRequestsView />} />
            <Route path={ROUTES.DEALERS.slice(1)} element={<DealersView />} />
          </Route>

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route path={ROUTES.DASHBOARD} element={<DashboardLayout />}>
              <Route index element={<DashboardView />} />
              <Route path="pending-requests" element={<PendingRequestsView />} />

              <Route path="bookings" element={<BookingsView />} />
              <Route path="bookings/:id" element={<BookingDetailView />} />
              <Route path="resources" element={<ResourcesView />} />
              <Route path="customers" element={<CustomersView />} />
              <Route path="services" element={<ServicesView />} />
              <Route path="knowledge-base" element={<KnowledgeBaseView />} />
              <Route path="chat-logs" element={<ChatLogsView />} />
              <Route path="forms" element={<FormsView />} />
              <Route path="forms/create" element={<FormCreateView />} />
              <Route path="forms/:formId" element={<FormDetailView />} />
              <Route path="forms/edit/:formId" element={<FormEditView />} />
              <Route path="phone" element={<PhoneView />} />
              <Route path="phone/routing/:phoneNumberId" element={<RoutingView />} />
              <Route path="personalization" element={<PersonalizationSettings />} />
              <Route path="subscriptions" element={<BillingView />} />
              <Route path="payment/*" element={<PaymentSettingsView />} />
              {role !== AUTH_CONSTANTS.ROLES.USER ? (
                <Route path="settings/*" element={<SettingsView />} />
              ) : null}
            </Route>
          </Route>

          {/* Catch-all: redirect unknown routes to home */}
          <Route path="*" element={<Navigate to={ROUTES.ROOT} replace />} />
        </Routes>
      </Elements>
    </ThemeProvider>
  );
}
